import api from './api';

export interface SchedulerTask {
  id: number;
  task_key: string;
  name: string;
  description?: string;
  schedule_type: 'interval' | 'cron';
  schedule_label: string;
  category: string;
  is_enabled: boolean;
  last_run_at?: string;
  last_status?: 'success' | 'error' | 'skipped' | 'running';
  last_duration_ms?: number;
  last_message?: string;
  run_count: number;
  error_count: number;
  sort_order: number;
}

export interface SchedulerHeartbeat {
  process_id?: string;
  hostname?: string;
  pid?: number;
  started_at?: string;
  last_heartbeat_at?: string;
  timezone?: string;
}

export interface SchedulerTaskLog {
  id: number;
  task_key: string;
  status: 'success' | 'error' | 'skipped';
  message?: string;
  details?: Record<string, unknown>;
  duration_ms?: number;
  trigger_type: 'scheduled' | 'manual';
  started_at: string;
  finished_at: string;
}

export interface SchedulerStatus {
  is_online: boolean;
  heartbeat: SchedulerHeartbeat | null;
  tasks: SchedulerTask[];
  stats: {
    total_tasks: number;
    enabled_tasks: number;
    errors_24h: number;
  };
}

class SchedulerService {
  async getStatus(): Promise<SchedulerStatus> {
    const res = await api.get('/scheduler/status');
    return res.data.data;
  }

  async getTasks(): Promise<SchedulerTask[]> {
    const res = await api.get('/scheduler/tasks');
    return res.data.data;
  }

  async getLogs(params: {
    page?: number;
    limit?: number;
    status?: string;
    task_key?: string;
  } = {}) {
    const search = new URLSearchParams();
    if (params.page) search.set('page', String(params.page));
    if (params.limit) search.set('limit', String(params.limit));
    if (params.status) search.set('status', params.status);
    if (params.task_key) search.set('task_key', params.task_key);
    const res = await api.get(`/scheduler/logs?${search.toString()}`);
    return res.data.data;
  }

  async getTaskLogs(
    taskKey: string,
    params: { page?: number; limit?: number; status?: string } = {}
  ) {
    const search = new URLSearchParams();
    if (params.page) search.set('page', String(params.page));
    if (params.limit) search.set('limit', String(params.limit));
    if (params.status) search.set('status', params.status);
    const res = await api.get(`/scheduler/tasks/${taskKey}/logs?${search.toString()}`);
    return res.data.data;
  }

  async toggleTask(taskKey: string, isEnabled: boolean) {
    const res = await api.patch(`/scheduler/tasks/${taskKey}`, { is_enabled: isEnabled });
    return res.data;
  }

  async runTask(taskKey: string) {
    const res = await api.post(`/scheduler/tasks/${taskKey}/run`);
    return res.data;
  }
}

export default new SchedulerService();
